import React, { memo } from 'react';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { NumberControlUpdateHandler } from './number-controls';
import { useTranslations } from 'next-intl';
import { LineWeight } from '@crabs-ai/web-icons';
import { ValueInputField } from '@crabs-ai/ds-react';
import { useSelectionValues } from '../utils/use-selection-values';
import { TextItem } from '../../items/text/text-item-type';
import { CaptionsItem } from '../../items/captions/captions-item-type';
import { SolidItem } from '../../items/solid/solid-item-type';

export const StrokeWidthControlsUnmemoized: React.FC<{
  strokeWidth: number;
  itemId: string;
}> = ({ strokeWidth, itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);
  const { isMixed } = useSelectionValues<
    TextItem | CaptionsItem | SolidItem,
    number
  >(itemId, (item) => (item as TextItem).strokeWidth ?? 0);

  const onStrokeWidth: NumberControlUpdateHandler = React.useCallback(
    ({ num, commitToUndoStack }) => {
      setState({
        update: (state) => {
          return changeItemsWithSelection(state, itemId, targetIds, (i) => {
            if (
              i.type !== 'text' &&
              i.type !== 'captions' &&
              i.type !== 'solid'
            ) {
              throw new Error('Item is not a text, caption, or solid');
            }
            return {
              ...i,
              strokeWidth: num,
            };
          });
        },
        commitToUndoStack,
      });
    },
    [setState, itemId, targetIds],
  );

  return (
    <div className="w-full">
      <ValueInputField
        isShowLabel={true}
        label={t('Editor.inspector.text.stroke-width')}
        isShowLeadingNode={true}
        leadingNodeType="icon"
        leadingNodeValue={
          <LineWeight className="size-[20px] [&_path]:fill-sys-cont-neutral-default" />
        }
        setValue={onStrokeWidth}
        value={strokeWidth}
        min={0}
        max={100}
        step={1}
        accessibilityLabel="Stroke width"
        hasMixedValue={isMixed}
        className="w-full"
      />
    </div>
  );
};

export const StrokeWidthControls = memo(StrokeWidthControlsUnmemoized);
